import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { lastDayOfMonth, startOfMonth, subMonths } from 'date-fns'
import {
  InsightsFiltersOptionsDocument,
  InsightsFiltersOptionsQuery,
} from 'generated/resources/graphql'

import AuthorizeUser from 'modules/core/components/AuthorizeUser'
import { DateNavigator } from 'modules/core/components/DateNavigator'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import Filters from 'modules/insights/components/Filters'
import { HeadcountTable } from 'modules/insights/components/HeadcountTable'
import ChartHeadcountEvolution from 'modules/insights/components/WidgetHeadcountEvolution/ChartHeadcountEvolution'
import { HeadcountCounters } from 'modules/insights/components/WidgetHeadcountEvolution/HeadcountCounters'
import { CycleDateRangeWithSetters } from 'modules/insights/hooks/useCycleDateRange'
import { InsightsFilters, useInsightsFilters } from 'modules/insights/hooks/useInsightsFilters'
import { SubRangeSelectionAction } from 'modules/insights/lib/events'
import type HeadcountEvolution from 'modules/insights/lib/metrics/headcountEvolution'
import { useSelectedRange } from 'modules/insights/pages/HeadcountPage/useSelectedRange'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import { useHeadcountPageQuery } from './useHeadcountPageQuery'

type PageContentProps = {
  headcountEvolution: HeadcountEvolution
  filterOptionsData: InsightsFiltersOptionsQuery
  cycleDateRange: CycleDateRangeWithSetters
  isLoading: boolean
  showExportModal: boolean
  filters: InsightsFilters
  setFilter: <T extends keyof InsightsFilters>(filterType: T, newFilter: InsightsFilters[T]) => void
  clearFilters: () => void
  onShowExportModal: () => void
  onCloseExportModal: () => void
}

const PageContent = ({
  headcountEvolution,
  filterOptionsData,
  cycleDateRange,
  isLoading,
  filters,
  setFilter,
  clearFilters,
  showExportModal,
  onShowExportModal,
  onCloseExportModal,
}: PageContentProps) => {
  const { selectedRange, setSelectedRange } = useSelectedRange(headcountEvolution)

  const headcountEvolutionData = headcountEvolution.generateHeadcountEvolution()

  const handleXAxisSelected = useUnsafeCallback(
    (date: Date, action: SubRangeSelectionAction) => {
      track(trackingEvents.insights.headcountPage.subRangeSelected, {
        action,
      })

      setSelectedRange(date)
    },
    [setSelectedRange]
  )

  return (
    <Box flexDirection="column" gap="s48">
      <Box flexDirection="row" gap="s16" justifyContent="spaceBetween" flexWrap="wrap">
        <Filters
          filters={filters}
          setFilter={setFilter}
          isLoading={isLoading}
          clearFilters={clearFilters}
          filterOptionsData={filterOptionsData}
        />
        <Box flexDirection="row" gap="s8">
          <DateNavigator
            cycle={cycleDateRange.cycle}
            startDate={cycleDateRange.startDate}
            endDate={cycleDateRange.endDate}
            onCycleChange={cycleDateRange.setCycle}
            onDateRangeChange={cycleDateRange.setDateRange}
          />
          <Button type="primary" iconName="download" onClick={() => onShowExportModal()}>
            {i18n.t('common.export')}
          </Button>
        </Box>
      </Box>

      <Box flexDirection="row" gap="s40" paddingX="s32" alignItems="center">
        <HeadcountCounters headcountEvolution={headcountEvolution} date={selectedRange} />

        <Box minWidth="none" flexGrow>
          <ChartHeadcountEvolution
            cycle={cycleDateRange.cycle}
            headcount={headcountEvolutionData}
            headcountEvolution={headcountEvolution.headcountEvolution}
            onXAxisSelected={handleXAxisSelected}
            selectedXAxis={selectedRange}
          />
        </Box>
      </Box>

      <HeadcountTable
        data={headcountEvolution.generateHeadcountEvolution()}
        headcountEvolution={headcountEvolution.headcountEvolution}
        joiners={headcountEvolution.joiners}
        leavers={headcountEvolution.leavers}
        selectedRange={selectedRange}
        showExportModal={showExportModal}
        onChangeSelectedRange={(newSelectedRange) =>
          handleXAxisSelected(newSelectedRange, 'date-range-navigator')
        }
        onCloseExportModal={onCloseExportModal}
      />
    </Box>
  )
}

const HeadcountPage = () => {
  const [showExportModal, setShowExportModal] = React.useState(false)
  const { data: filterOptionsData, error: filterOptionsError } = useQuery(
    InsightsFiltersOptionsDocument,
    {
      variables: {
        // Taking only the last year values
        fromDate: dateToString(startOfMonth(subMonths(new Date(), 11)), 'backend'),
        toDate: dateToString(lastDayOfMonth(new Date()), 'backend'),
      },
    }
  )
  const { filters, setFilter, clearFilters } = useInsightsFilters()
  const { headcountEvolution, loading, error, cycleDateRange } = useHeadcountPageQuery(filters)

  const canSeeInsights = filterOptionsData?.permissions?._policies.insightsSeeInsights.can ?? false

  return (
    <AuthorizeUser authorizeFn={() => canSeeInsights}>
      <ResponsivePage.Body
        header={{
          title: i18n.t('employees.updates.title'),
          description: i18n.t('employees.updates.description'),
        }}
        loading={!headcountEvolution}
        error={error ?? filterOptionsError}
        variant="full"
      >
        {headcountEvolution && filterOptionsData && (
          <PageContent
            headcountEvolution={headcountEvolution}
            filterOptionsData={filterOptionsData}
            cycleDateRange={cycleDateRange}
            filters={filters}
            setFilter={setFilter}
            clearFilters={clearFilters}
            isLoading={loading}
            showExportModal={showExportModal}
            onShowExportModal={() => setShowExportModal(true)}
            onCloseExportModal={() => setShowExportModal(false)}
          />
        )}
      </ResponsivePage.Body>
    </AuthorizeUser>
  )
}

export default HeadcountPage
